import { ActionTree } from 'vuex';
import { IVideoLibraryState } from '@/video/library/store/video-library.types';
import { RootState } from '@/store/store';
import { IVideo } from '@/types/Video';
import { IVideoLibraryResponseDTO } from '@/api/video/dtos/video-library-response-dto';
import { VideoService } from '@/api/video/services/video.service';
import { translate } from '@/helpers/translation.helper';
import { VideoLibraryHelper } from '@/video/library/helpers/video-library.helper';
import {
  EVideoLibraryAction,
  EVideoLibraryMutation,
  state as defaultState,
} from '@/video/library/store/video-library.constants';

export const actions: ActionTree<IVideoLibraryState, RootState> = {
  async [EVideoLibraryAction.LoadVideos](
    { dispatch, commit, state },
    { currentPage, saved }: { currentPage: number; saved: boolean }
  ) {
    const { pageSize, totalRows } = state.pagination;
    const margin: number = pageSize * (currentPage - 1);
    commit(EVideoLibraryMutation.SetLoading, true);
    commit(EVideoLibraryMutation.SetPagination, {
      pageMargin: margin,
      pageSize,
      currentPage,
      totalRows,
    });
    try {
      const videosResponse: IVideoLibraryResponseDTO = await VideoService.loadVideos(
        state.search,
        margin,
        pageSize - 1, // backend returns array based on last index
        saved
      );
      dispatch(EVideoLibraryAction.LoadVideosSuccess, videosResponse);
    } catch (error) {
      dispatch(EVideoLibraryAction.LoadVideosFailed, error);
    }
  },
  [EVideoLibraryAction.LoadVideosSuccess](
    { commit },
    { videos, totalCount }: IVideoLibraryResponseDTO
  ) {
    commit(EVideoLibraryMutation.SetLoading, false);
    commit(EVideoLibraryMutation.SetLoaded, true);
    commit(EVideoLibraryMutation.SetVideos, { videos, totalCount });
  },
  [EVideoLibraryAction.LoadVideosFailed]({ dispatch }) {
    dispatch('alert/showDangerAlert', translate('Failed-to-load-videos'), {
      root: true,
    });
  },
  async [EVideoLibraryAction.DeleteVideo]({ state, dispatch, commit }, videoId: string) {
    const originalVideos: IVideo[] = state.videos;
    commit(EVideoLibraryMutation.DeleteVideo, videoId);
    try {
      await VideoService.deleteVideo(videoId);
    } catch (error) {
      dispatch(EVideoLibraryAction.DeleteVideoFailed, originalVideos);
    }
  },
  [EVideoLibraryAction.DeleteVideoFailed]({ dispatch, commit, state }, originalVideos: IVideo[]) {
    commit(EVideoLibraryMutation.SetVideos, {
      videos: originalVideos,
      totalCount: state.pagination.totalRows + 1,
    });
    dispatch('alert/showDangerAlert', translate('Failed-to-delete-video'), {
      root: true,
    });
  },
  async [EVideoLibraryAction.CopyLinkToClipboard]({ dispatch }, link: string) {
    try {
      await VideoLibraryHelper.copyLinkToClipboard(link);
      dispatch('alert/showSuccessAlert', translate('Copied-link-to-clipboard'), {
        root: true,
      });
    } catch (error) {
      dispatch('alert/showDangerAlert', translate('Failed-to-copy-link-to-clipboard'), {
        root: true,
      });
    }
  },
  async [EVideoLibraryAction.ShareVideo]({ dispatch }, id: string) {
    try {
      await VideoService.shareVideo(id);
    } catch (error) {
      dispatch('alert/showDangerAlert', translate('Failed-to-share-video'), {
        root: true,
      });
    }
  },
  async [EVideoLibraryAction.UpdateVideo]({ dispatch, commit, state }, video: IVideo) {
    dispatch(EVideoLibraryAction.SetEditId, null);
    const originalVideo: IVideo = state.videos.find(stateVideo => stateVideo.id === video.id);
    commit(EVideoLibraryMutation.UpdateVideo, video);
    try {
      await VideoService.updateVideo({
        videoTitle: video.videoTitle,
        id: video.id,
      });
    } catch (error) {
      dispatch('alert/showDangerAlert', translate('Failed-to-update-video'), {
        root: true,
      });
      commit(EVideoLibraryMutation.UpdateVideo, originalVideo);
    }
  },
  [EVideoLibraryAction.Search]({ commit, dispatch }, query: string) {
    commit(EVideoLibraryMutation.SetSearch, query);
    dispatch(EVideoLibraryAction.LoadVideos, {
      currentPage: defaultState.pagination.currentPage,
      saved: true,
    });
  },
  [EVideoLibraryAction.ToggleDeleteModal]({ commit }, videoIdToDelete: string) {
    commit(EVideoLibraryMutation.SetDeleteModalVisibility, videoIdToDelete);
  },
  [EVideoLibraryAction.SetEditId]({ commit }, videoIdToEdit: string) {
    commit(EVideoLibraryMutation.SetEditId, videoIdToEdit);
  },
};
